/**
 * Čo sa pri priraďovaní priraďuje (KOLO 216).
 *
 * User: *„keď vyberám materiály a priraďujem k danej hrúbke materiál, nech
 * si viem pozrieť, ktoré dielce a v akej zostave majú priradenú túto
 * hrúbku. Alebo aj kovania, že vidím, v ktorej zostave je toto kovanie…
 * pri hranách nech vidíš, koľko m je tej hrany."* Údaje dáva analýza
 * (`vyskyty`, `dlzka_mm`); tu je len text, aby tabuľka aj výberové okno
 * hovorili to isté.
 */
import type { MapovaniePolozka } from "@/api/hooks/useTopsolidRaw";

export interface ZostavaVyskytov {
  /** Určení zostavy z exportu (`Skrinka_A`). */
  zostava: string;
  /** Meno z Tandem Zostáv (`TAN_A`), ak prišlo. */
  meno: string | null;
  /** Kusov spolu (dielcov alebo kovania). */
  kusov: number;
  /** „bok_L 2×, dno 1×" — pri kovaní prázdne. */
  dielce: string;
}

/** Číslo s čiarkou bez zbytočných núl — `12,4`. */
function cislo(n: number, desatinne = 1): string {
  return Number(n.toFixed(desatinne)).toString().replace(".", ",");
}

/**
 * Dĺžka hrany v metroch — „12,4 m", pod meter „0,85 m"; bez dĺžky prázdne
 * (starší server ju neposiela — prázdna bunka nie je „—", ktoré mýli).
 */
export function metreHrany(
  dlzkaMm: string | number | null | undefined,
): string {
  const mm = Number(dlzkaMm ?? 0);
  if (!Number.isFinite(mm) || mm <= 0) return "";
  return `${cislo(mm / 1000, mm < 1000 ? 2 : 1)} m`;
}

/** Výskyty po zostavách v poradí zo súboru. */
export function vyskytyPoZostavach(
  p: MapovaniePolozka,
  mena?: Record<string, string> | null,
): ZostavaVyskytov[] {
  const poradie: string[] = [];
  const skupiny = new Map<
    string,
    { kusov: number; dielce: Map<string, number> }
  >();
  for (const v of p.vyskyty ?? []) {
    let s = skupiny.get(v.zostava_kod);
    if (!s) {
      s = { kusov: 0, dielce: new Map() };
      skupiny.set(v.zostava_kod, s);
      poradie.push(v.zostava_kod);
    }
    s.kusov += v.pocet_ks;
    if (v.dielec)
      s.dielce.set(v.dielec, (s.dielce.get(v.dielec) ?? 0) + v.pocet_ks);
  }
  return poradie.map((z) => {
    const s = skupiny.get(z)!;
    return {
      zostava: z,
      meno: mena?.[z] ?? null,
      kusov: s.kusov,
      dielce: [...s.dielce].map(([d, ks]) => `${d} ${ks}×`).join(", "),
    };
  });
}

/** „Skrinka_A (TAN_A)" — Určení vždy, meno z Tandem Zostáv navyše. */
export function nazovZostavy(z: ZostavaVyskytov): string {
  return z.meno && z.meno !== z.zostava
    ? `${z.zostava} (${z.meno})`
    : z.zostava;
}

/**
 * Jedna veta do výberového okna — „V exporte: 14 dielcov v 3 zostavách
 * (Skrinka_A, …)", pri kovaní kusy po zostavách, pri hrane metre.
 */
export function vetaKontextu(
  p: MapovaniePolozka,
  mena?: Record<string, string> | null,
): string | null {
  if (p.druh === "hrana") {
    const m = metreHrany(p.dlzka_mm);
    return m ? `V exporte: ${m} tejto hrany.` : null;
  }
  const zostavy = vyskytyPoZostavach(p, mena);
  if (zostavy.length === 0) return null;
  const kusov = zostavy.reduce((a, z) => a + z.kusov, 0);
  const vZostavach =
    zostavy.length === 1 ? "v zostave" : `v ${zostavy.length} zostavách`;
  if (p.druh === "kovanie") {
    const rozpis = zostavy
      .map((z) => `${nazovZostavy(z)} ${z.kusov} ks`)
      .join(", ");
    return `V exporte: ${kusov} ks ${vZostavach} — ${rozpis}.`;
  }
  const dielcov = (p.vyskyty ?? []).length;
  const slovo = dielcov === 1 ? "dielec" : dielcov < 5 ? "dielce" : "dielcov";
  const mena3 = zostavy.slice(0, 3).map(nazovZostavy).join(", ");
  const dalsie = zostavy.length > 3 ? ` a ďalšie ${zostavy.length - 3}` : "";
  return `V exporte: ${dielcov} ${slovo} ${vZostavach} (${mena3}${dalsie}).`;
}
